import React, { useRef } from "react";
import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  moveCropRectInsideImage,
  moveImageFrame,
  resizeCropRectInsideImage,
} from "../../../../../components/shared/imageToolbar/imageCropModel";
import { resolveCropPrepFrames } from "../../../lib/cropPrepModel";
import {
  enforceSquareHostCrop,
  resolveCircleCropPrepFrames,
  resolveCircleHostAspectRatio,
} from "../../../lib/circleCropPrepModel";
import "./CropOverlay.css";

const CORNERS = ["nw", "ne", "sw", "se"];
const EDGES = ["n", "s", "e", "w"];

const pctBox = (frame) => ({
  left: `${frame.x * 100}%`,
  top: `${frame.y * 100}%`,
  width: `${frame.w * 100}%`,
  height: `${frame.h * 100}%`,
});

/**
 * Interactive crop overlay (image pan/zoom + crop window).
 */
const CropOverlay = ({
  imageUrl,
  cropFrame = DEFAULT_CROP_RECT,
  imageFrame = DEFAULT_IMAGE_FRAME,
  aspectRatio = null,
  freeform = false,
  circleMode = false,
  zoom = 100,
  width,
  height,
  onFramesChange = () => {},
  disabled = false,
}) => {
  const hostRef = useRef(null);
  const dragRef = useRef(null);

  const resolvedFrames = circleMode
    ? resolveCircleCropPrepFrames(cropFrame, imageFrame, width, height)
    : resolveCropPrepFrames(cropFrame, imageFrame);
  const { cropFrame: crop, imageFrame: image } = resolvedFrames;

  const circleAspect =
    circleMode && width > 0 && height > 0
      ? resolveCircleHostAspectRatio(width, height)
      : aspectRatio;

  const cropInImage = {
    x: (crop.x - image.x) / image.w,
    y: (crop.y - image.y) / image.h,
    w: crop.w / image.w,
    h: crop.h / image.h,
  };

  const emitFrames = (nextCrop, nextImage) => {
    const resolved = circleMode
      ? resolveCircleCropPrepFrames(nextCrop, nextImage, width, height)
      : resolveCropPrepFrames(nextCrop, nextImage);
    onFramesChange(resolved.cropFrame, resolved.imageFrame);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || disabled) return;
    const host = hostRef.current;
    if (!host) return;
    const bounds = host.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const dx = (e.clientX - drag.startClientX) / bounds.width;
    const dy = (e.clientY - drag.startClientY) / bounds.height;

    if (drag.type === "crop-move") {
      emitFrames(
        moveCropRectInsideImage(drag.startCrop, dx, dy, drag.startImage),
        drag.startImage
      );
    } else if (drag.type === "crop-resize") {
      let nextCrop = resizeCropRectInsideImage(
        drag.startCrop,
        drag.handle,
        dx,
        dy,
        circleMode ? circleAspect : aspectRatio,
        drag.startImage
      );
      if (circleMode && width > 0 && height > 0) {
        nextCrop = enforceSquareHostCrop(
          nextCrop,
          width,
          height,
          drag.startImage
        );
      }
      emitFrames(nextCrop, drag.startImage);
    } else if (drag.type === "image-move") {
      emitFrames(
        drag.startCrop,
        moveImageFrame(drag.startImage, dx, dy, drag.startCrop)
      );
    }
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    dragRef.current = null;
  };

  const startDrag = (type, e, handle = "") => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      type,
      handle,
      startCrop: { ...crop },
      startImage: { ...image },
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  };

  const canPanImage = Number(zoom) > 100;
  const handles = circleMode || !freeform ? CORNERS : [...CORNERS, ...EDGES];
  const overlayClass = circleMode
    ? "crop-overlay crop-overlay--circle"
    : "crop-overlay";

  return (
    <div
      className="crop-overlay-wrap"
      style={{ width, height }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div ref={hostRef} className={overlayClass} style={{ width, height }}>
        <div className="crop-overlay__image-layer" style={pctBox(image)}>
          <img
            src={imageUrl}
            alt=""
            className="crop-overlay__img"
            draggable={false}
          />
          <div
            className={`crop-overlay__dim${
              circleMode ? " crop-overlay__dim--circle" : ""
            }`}
            style={pctBox(cropInImage)}
            aria-hidden="true"
          />
        </div>

        {canPanImage ? (
          <div
            className="crop-overlay__image-boundary"
            style={pctBox(image)}
            onPointerDown={(e) => startDrag("image-move", e)}
          />
        ) : null}

        <div
          className={`crop-overlay__crop-layer${
            circleMode ? " crop-overlay__crop-layer--circle" : ""
          }`}
          style={pctBox(crop)}
          onPointerDown={(e) => {
            if (e.target.closest(".crop-overlay__handle")) return;
            startDrag("crop-move", e);
          }}
        >
          {handles.map((handle) => (
            <span
              key={handle}
              className={`crop-overlay__handle crop-overlay__handle--${handle}`}
              onPointerDown={(e) => startDrag("crop-resize", e, handle)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CropOverlay;
